import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ISSUE_PRIORITIES, ISSUE_STATUSES } from '../../shared/schema.ts';
import type { IssuePriority, IssueStatus } from '../../shared/schema.ts';
import { mutate, useUser } from '../api.ts';
import { ago, issueKey } from '../format.ts';
import { useLive } from '../live.ts';
import { nameOf } from '../types.ts';
import type { Comment, IssueDetail, User } from '../types.ts';
import { PriorityLabel, StatusTag, TagChip } from './Marks.tsx';
import { Picker } from './Picker.tsx';

/**
 * One issue and its whole thread, as ONE live query. Every control here is a write; none of them
 * updates the pane itself. The pane changes because the live query did — which is also how it
 * changes when somebody else edits the same issue in another window.
 */
export function Detail({ id, onClose, onSearchTag }: { id: string; onClose: () => void; onSearchTag: (name: string) => void }) {
  const user = useUser();
  const live = useLive<IssueDetail | null>('detail', { id });
  const users = useLive<User[]>('users').data ?? [];
  const issue = live.stale ? undefined : live.data;

  if (live.error) return <Shell onClose={onClose}><p className="empty">Could not open this issue: {live.error.message}</p></Shell>;
  if (issue === undefined) return <Shell onClose={onClose}><p className="empty">Opening a live query…</p></Shell>;
  if (issue === null) return <Shell onClose={onClose}><p className="empty">This issue does not exist — or someone just deleted it.</p></Shell>;

  const comments = issue.comments ?? [];
  const [description, ...discussion] = comments;
  const owners = users.some(u => u.id === issue.ownerId) ? users : [...users, { id: issue.ownerId, name: nameOf(issue.owner, issue.ownerId) }];
  const isOwner = issue.ownerId === user;

  return (
    <Shell onClose={onClose} label={issueKey(issue.id)}>
      <Title id={issue.id} title={issue.title} />
      <dl className="fields">
        <dt className="micro">STATUS</dt>
        <dd>
          <Picker
            label="Status" value={issue.status} options={ISSUE_STATUSES}
            render={(s: IssueStatus) => <StatusTag status={s} />}
            onChange={status => mutate('setStatus', { id: issue.id, status })}
          />
        </dd>
        <dt className="micro">PRIORITY</dt>
        <dd>
          <Picker
            label="Priority" value={issue.priority} options={ISSUE_PRIORITIES}
            render={(p: IssuePriority) => <PriorityLabel priority={p} />}
            onChange={priority => mutate('setPriority', { id: issue.id, priority })}
          />
        </dd>
        <dt className="micro">OWNER</dt>
        <dd>
          <select value={issue.ownerId} aria-label="Owner" onChange={e => mutate('setOwner', { id: issue.id, owner: e.target.value })}>
            {owners.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </dd>
        <dt className="micro">TAGS</dt>
        <dd className="tag-field">
          {(issue.tags ?? []).map(tag => (
            <TagChip
              key={tag.id} name={tag.name}
              onSearch={() => onSearchTag(tag.name)}
              onRemove={() => mutate('removeTag', { id: tag.id, issueId: issue.id })}
            />
          ))}
          <AddTag issueId={issue.id} />
        </dd>
        <dt className="micro">UPDATED</dt>
        <dd className="muted">{ago(issue.updatedAt)}</dd>
      </dl>

      <section className="thread" aria-label="Description and comments">
        {description && <CommentView comment={description} user={user} isDescription />}
        <h3 className="eyebrow">{discussion.length} {discussion.length === 1 ? 'COMMENT' : 'COMMENTS'}</h3>
        {discussion.map(c => <CommentView key={c.id} comment={c} user={user} />)}
        <Composer issueId={issue.id} />
      </section>

      <footer className="detail-foot">
        <button
          type="button" className="danger" disabled={!isOwner}
          title={isOwner ? 'Delete this issue and its thread' : `Only ${nameOf(issue.owner, issue.ownerId)} can delete this. Switch to them in the user menu to try.`}
          onClick={async () => { if (confirm('Delete this issue for everyone?')) { await mutate('deleteIssue', { id: issue.id }); } }}
        >Delete issue</button>
        {!isOwner && <span className="micro muted">OWNER ONLY — THE SERVER CHECKS</span>}
      </footer>
    </Shell>
  );
}

function Shell({ children, onClose, label }: { children: ReactNode; onClose: () => void; label?: string }) {
  return (
    <div className="detail-inner">
      <header className="pane-head">
        <span className="eyebrow">{label ? `ISSUE / ${label}` : 'ISSUE'}</span>
        <button type="button" className="close" aria-label="Close" onClick={onClose}>×</button>
      </header>
      {children}
    </div>
  );
}

function Title({ id, title }: { id: string; title: string }) {
  const [draft, setDraft] = useState<string | null>(null);
  if (draft === null) return <h2 className="detail-title"><button type="button" className="title-button" onClick={() => setDraft(title)} title="Edit title">{title}</button></h2>;
  const save = async () => { const next = draft.trim(); setDraft(null); if (next && next !== title) await mutate('editTitle', { id, title: next }); };
  return (
    <form className="title-form" onSubmit={e => { e.preventDefault(); save(); }}>
      <input autoFocus value={draft} maxLength={180} aria-label="Title" onChange={e => setDraft(e.target.value)} onBlur={save} onKeyDown={e => { if (e.key === 'Escape') setDraft(null); }} />
    </form>
  );
}

function AddTag({ issueId }: { issueId: string }) {
  const [draft, setDraft] = useState('');
  return (
    <form className="add-tag" onSubmit={async e => { e.preventDefault(); const name = draft.trim(); if (!name) return; setDraft(''); await mutate('addTag', { issueId, name }); }}>
      <input value={draft} onChange={e => setDraft(e.target.value)} placeholder="+ tag" aria-label="Add a tag" maxLength={28} />
    </form>
  );
}

function CommentView({ comment, user, isDescription }: { comment: Comment; user: string; isDescription?: boolean }) {
  const [draft, setDraft] = useState<string | null>(null);
  const mine = comment.authorId === user;
  return (
    <article className={isDescription ? 'comment description' : 'comment'}>
      <header>
        <strong>{nameOf(comment.author, comment.authorId)}</strong>
        <span className="micro muted">{isDescription ? 'OPENED ' : ''}{ago(comment.createdAt).toUpperCase()}</span>
        {mine && draft === null && <button type="button" className="link micro" onClick={() => setDraft(comment.body)}>EDIT</button>}
      </header>
      {draft === null ? <p>{comment.body}</p> : (
        <form onSubmit={async e => { e.preventDefault(); const next = draft.trim(); setDraft(null); if (next && next !== comment.body) await mutate('editComment', { id: comment.id, body: next }); }}>
          <textarea autoFocus value={draft} rows={3} maxLength={2000} onChange={e => setDraft(e.target.value)} aria-label="Edit comment" />
          <div className="row-actions"><button type="submit" className="button small">Save</button><button type="button" className="link" onClick={() => setDraft(null)}>Cancel</button></div>
        </form>
      )}
    </article>
  );
}

function Composer({ issueId }: { issueId: string }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(''), [issueId]);
  return (
    <form className="composer" onSubmit={async e => {
      e.preventDefault();
      const body = draft.trim();
      if (!body) return;
      setBusy(true);
      const ok = await mutate('addComment', { issueId, body });
      setBusy(false);
      if (ok !== undefined) setDraft('');
    }}>
      <textarea value={draft} rows={3} maxLength={2000} placeholder="Add a comment. Everyone watching this issue sees it arrive." aria-label="Add a comment" onChange={e => setDraft(e.target.value)} />
      <button type="submit" className="button small" disabled={busy || !draft.trim()}>Comment <span aria-hidden="true">→</span></button>
    </form>
  );
}
